
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { distributionCenterPriceService } from '@/services/distributionCenterPriceService';
import { CreateDistributionCenterPriceInput, DistributionCenterPriceFilters } from '@/types/database/distribution-center-prices';
import { useToast } from '@/hooks/use-toast';

export const useDistributionCenterPrices = (filters?: DistributionCenterPriceFilters) => {
  return useQuery({
    queryKey: ['distribution-center-prices', filters],
    queryFn: () => distributionCenterPriceService.getDistributionCenterPrices(filters),
  });
};

export const useCreateDistributionCenterPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (price: CreateDistributionCenterPriceInput) =>
      distributionCenterPriceService.createDistributionCenterPrice(price),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-center-prices'] });
      toast({
        title: 'Success',
        description: 'Distribution center price created successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to create price',
        variant: 'destructive',
      });
    },
  });
};

export const useBulkCreateDistributionCenterPrices = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (prices: CreateDistributionCenterPriceInput[]) =>
      distributionCenterPriceService.bulkCreateDistributionCenterPrices(prices),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['distribution-center-prices'] });
      toast({
        title: 'Success',
        description: `Successfully uploaded ${data.length} distribution center prices`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to upload prices',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateDistributionCenterPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, price }: { id: string; price: Partial<CreateDistributionCenterPriceInput> }) =>
      distributionCenterPriceService.updateDistributionCenterPrice(id, price),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-center-prices'] });
      toast({
        title: 'Success',
        description: 'Distribution center price updated successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update price',
        variant: 'destructive',
      });
    },
  });
};

export const useDeleteDistributionCenterPrice = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => distributionCenterPriceService.deleteDistributionCenterPrice(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['distribution-center-prices'] });
      toast({
        title: 'Success',
        description: 'Distribution center price deleted successfully',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to delete price',
        variant: 'destructive',
      });
    },
  });
};
